import { createSlice } from "@reduxjs/toolkit";
import { newId } from "../utils/id";

const initialTilesLimits = { workspaces: 1, workspace_tiles: 3, max_zoom: 18 };
const initialWorkspaces = Array.from(
  { length: initialTilesLimits.workspaces },
  (_, i) => i + 1,
).reduce((acc, i) => {
  acc[i] = [];
  return acc;
}, {});

const mapSlice = createSlice({
  name: "map",
  initialState: () => ({
    sessionKey: newId(),
    fieldsLayerKey: newId(),
    ownersLayerKey: newId(),
    cropTypes: [], // [ {id, name_ar, name_en} ]
    fieldsLegends: [], // [ {name, key, legends} ]
    irrigationMethods: [], // [ {id, name_ar, name_en} ]
    restoreView: false,
    mapCenter: null,
    mapZoom: null,
    date: {
      imageDate: "",
    },
    currentField: null,
    tilesLimits: initialTilesLimits,
    workspaces: initialWorkspaces,
    currentWorkspace: 1,
    overlayMode: "status",
    fillVectors: false,
    targetBounds: null,
    staticLayers: {
      aoi: null,
    },
    mapBounds: null,
    boundaryMethod: "upload",
    drawingMode: null,
    drawnShape: null,
    editingEntity: null, // { id, properties }
    tileLayer: localStorage.getItem("tileLayer") || "defaultmap",
    tileBan: {
      show: false,
      banned: false,
      message: null,
      remainingSeconds: null,
    },
    satelliteTilesAvailable: {
      satelliteTiles: false,
      planetTiles: false,
      defaultmapTiles: false,
    },
    satelliteLayer: {
      tileId: newId(),
      tileUrl: null,
      tileUrlExpiresAt: 0,
      attribution: "",
    },
    planetLayer: {
      tileId: newId(),
      tileUrl: null,
      tileUrlExpiresAt: 0,
      attribution: "",
      failed: false,
    },
    defaultmapLayer: {
      tileId: newId(),
      tileUrl: null,
      tileUrlExpiresAt: 0,
      attribution: "",
    },
    addProjectSatelliteLayer: {
      tileId: newId(),
      tileUrl: null,
      tileUrlExpiresAt: 0,
      attribution: "",
    },
    addProjectPlanetLayer: {
      tileId: newId(),
      tileUrl: null,
      tileUrlExpiresAt: 0,
      attribution: "",
      failed: false,
    },
  }),

  reducers: {
    setOverlayMode: (state, action) => {
      state.overlayMode = action.payload;
    },
    reverseFillVectors: (state) => {
      state.fillVectors = !state.fillVectors;
    },
    setTileLayer: (state, action) => {
      state.tileLayer = action.payload;
    },
    setTileBan: (state, action) => {
      const { message, remainingSeconds } = action.payload || {};
      state.tileBan = {
        show: true,
        banned: true,
        message: message || null,
        remainingSeconds: remainingSeconds ?? null,
      };
    },
    clearTileBan: (state) => {
      state.tileBan = {
        show: false,
        banned: false,
        message: null,
        remainingSeconds: null,
      };
    },
    hideTileBan: (state) => {
      state.tileBan = { ...state.tileBan, show: false };
    },
    setSatelliteTilesAvailable: (state, action) => {
      const { satellite_tiles, planet_tiles, defaultmap_tiles } =
        action.payload;
      state.satelliteTilesAvailable = {
        satelliteTiles: satellite_tiles,
        planetTiles: planet_tiles,
        defaultmapTiles: defaultmap_tiles,
      };
    },
    setTargetBounds: (state, action) => {
      state.targetBounds = action.payload;
    },
    clearTargetBounds: (state) => {
      state.targetBounds = null;
    },
    setCurrentField: (state, action) => {
      state.currentField = action.payload;
    },
    // Workspace actions
    setCurrentWorkspace: (state, action) => {
      state.currentWorkspace = action.payload;
    },
    setTilesLimits: (state, action) => {
      const { tile_max_zoom, tile_workspaces, tiles_per_workspace } =
        action.payload;
      state.tilesLimits = {
        workspaces: tile_workspaces,
        workspace_tiles: tiles_per_workspace,
        max_zoom: tile_max_zoom,
      };

      // Reset workspaces to match the new workspace count.
      state.workspaces = Array.from(
        { length: tile_workspaces },
        (_, i) => i + 1,
      ).reduce((acc, i) => {
        acc[i] = [];
        return acc;
      }, {});

      if (state.currentWorkspace > tile_workspaces) {
        state.currentWorkspace = 1;
      }
    },
    // Tiles actions - operate on current workspace
    addTile: (state, action) => {
      const currentTiles = state.workspaces[state.currentWorkspace];
      const existingTile = currentTiles.find(
        (a) =>
          JSON.stringify(a.coordinates) ===
          JSON.stringify(action.payload.coordinates),
      );
      if (existingTile) {
        return state;
      } else {
        state.workspaces[state.currentWorkspace] = [
          ...currentTiles,
          action.payload,
        ];
      }
    },
    setTiles: (state, action) => {
      const { tileUrl, bbox, tileName, tileId, tileParams, tileUrlExpiresAt } =
        action.payload;
      state.workspaces[state.currentWorkspace].unshift({
        tileId: tileId,
        tileUrl: tileUrl,
        bbox: bbox,
        tileName: tileName,
        visible: true,
        tileParams: tileParams,
        tileUrlExpiresAt: tileUrlExpiresAt || 0,
      });
    },
    updateTileUrl: (state, action) => {
      const { tileId, tileUrl, tileUrlExpiresAt } = action.payload;
      const tile = state.workspaces[state.currentWorkspace].find(
        (t) => t.tileId === tileId,
      );
      if (tile) {
        tile.tileId = newId();
        tile.tileUrl = tileUrl;
        tile.tileUrlExpiresAt = tileUrlExpiresAt || 0;
      }
    },
    setSatelliteTileTemplate: (state, action) => {
      const { tileUrlTemplate, attribution, expiresAt } = action.payload;
      state.satelliteLayer = {
        ...state.satelliteLayer,
        tileId: newId(),
        tileUrl: tileUrlTemplate,
        attribution: attribution,
        tileUrlExpiresAt: expiresAt || 0,
      };
    },
    setPlanetTileTemplate: (state, action) => {
      const { tileUrlTemplate, attribution, expiresAt } = action.payload;
      state.planetLayer = {
        ...state.planetLayer,
        tileId: newId(),
        tileUrl: tileUrlTemplate,
        attribution: attribution,
        tileUrlExpiresAt: expiresAt || 0,
        failed: false,
      };
    },
    setPlanetTileFailed: (state, action) => {
      state.planetLayer.failed = action.payload;
    },
    setDefaultMapTileTemplate: (state, action) => {
      const { tileUrlTemplate, attribution, expiresAt } = action.payload;
      state.defaultmapLayer = {
        ...state.defaultmapLayer,
        tileId: newId(),
        tileUrl: tileUrlTemplate,
        attribution: attribution,
        tileUrlExpiresAt: expiresAt || 0,
      };
    },
    setAddProjectSatelliteTileTemplate: (state, action) => {
      const { tileUrlTemplate, attribution, expiresAt } = action.payload;
      state.addProjectSatelliteLayer = {
        ...state.addProjectSatelliteLayer,
        tileId: newId(),
        tileUrl: tileUrlTemplate,
        attribution: attribution,
        tileUrlExpiresAt: expiresAt || 0,
      };
    },
    setAddProjectPlanetTileTemplate: (state, action) => {
      const { tileUrlTemplate, attribution, expiresAt } = action.payload;
      state.addProjectPlanetLayer = {
        ...state.addProjectPlanetLayer,
        tileId: newId(),
        tileUrl: tileUrlTemplate,
        attribution: attribution,
        tileUrlExpiresAt: expiresAt || 0,
        failed: false,
      };
    },
    setAddProjectPlanetTileFailed: (state, action) => {
      state.addProjectPlanetLayer.failed = action.payload;
    },
    toggleTileVisibility: (state, action) => {
      const { tileId, forceValue } = action.payload;
      const currentTiles = state.workspaces[state.currentWorkspace];
      const tile = currentTiles.find((a) => a.tileId === tileId);
      if (tile) {
        tile.visible = forceValue !== undefined ? forceValue : !tile.visible;
      }
    },
    setAllTilesVisibility: (state, action) => {
      const { visible } = action.payload;
      const currentTiles = state.workspaces[state.currentWorkspace];
      state.workspaces[state.currentWorkspace] = currentTiles.map((tile) => ({
        ...tile,
        visible: visible,
      }));
    },
    reorderTiles: (state, action) => {
      const { oldIndex, newIndex } = action.payload;
      if (oldIndex === newIndex) return;
      const currentTiles = [...state.workspaces[state.currentWorkspace]];
      const [movedTile] = currentTiles.splice(oldIndex, 1);
      currentTiles.splice(newIndex, 0, movedTile);
      state.workspaces[state.currentWorkspace] = currentTiles;
    },
    deleteTile: (state, action) => {
      const deletedIds = action.payload;
      const currentTiles = state.workspaces[state.currentWorkspace];
      state.workspaces[state.currentWorkspace] = currentTiles.filter((tile) => {
        return !deletedIds.includes(tile.tileId.toString());
      });
    },
    clearAllMapState: (state) => {
      state.workspaces[state.currentWorkspace] = [];
    },
    //date actions
    setImageDate: (state, action) => {
      state.date.imageDate = action.payload;
    },
    setFieldsLayerKey: (state, _) => {
      state.fieldsLayerKey = newId();
    },
    setOwnersLayerKey: (state, _) => {
      state.ownersLayerKey = newId();
    },
    setRestoreView: (state, action) => {
      state.restoreView = action.payload;
    },
    setMapCenter: (state, action) => {
      state.mapCenter = action.payload;
    },
    setMapZoom: (state, action) => {
      state.mapZoom = action.payload;
    },
    setAOILayer: (state, action) => {
      state.staticLayers.aoi = action.payload;
    },
    setCropTypes: (state, action) => {
      state.cropTypes = action.payload;
    },
    setFieldsLegends: (state, action) => {
      state.fieldsLegends = action.payload;
    },
    setIrrigationMethods: (state, action) => {
      state.irrigationMethods = action.payload;
    },
    setMapBounds: (state, action) => {
      state.mapBounds = action.payload;
    },
    setBoundaryMethod: (state, action) => {
      state.boundaryMethod = action.payload;
    },
    setDrawingMode: (state, action) => {
      state.drawingMode = action.payload;
      state.drawnShape = null;
    },
    setDrawnShape: (state, action) => {
      state.drawnShape = action.payload;
    },
    clearDrawnShape: (state) => {
      state.drawnShape = null;
    },
    clearAreaDrawing: (state) => {
      state.drawingMode = null;
      state.drawnShape = null;
    },
    setEditingEntity: (state, action) => {
      state.editingEntity = action.payload;
    },
    clearEditingEntity: (state) => {
      state.editingEntity = null;
    },
  },
});

/**
 * Returns the base layer that should actually be rendered given the user's
 * preference and current availability. Satellite is only shown when not
 * drawing; in draw mode Planet is used instead, falling back to the default
 * basemap when Planet is unavailable or has failed to load.
 */
export const getEffectiveBaseLayer = ({
  tileLayer,
  tileBanned,
  isDrawing,
  drawingMode,
  planetAvailable,
  planetTileUrl,
  planetFailed,
  satelliteAvailable,
  satelliteTileUrl,
  defaultmapAvailable,
  defaultmapTileUrl,
}) => {
  // The default is the defaultmap layer
  let disableSatellite = true;
  let effectiveLayer = null;
  let toggleLayer = null;
  const satelliteSelected = tileLayer === "satellite";
  if (!tileBanned) {
    if (!drawingMode || !isDrawing) {
      if (defaultmapAvailable && defaultmapTileUrl) {
        effectiveLayer = "defaultmap";
        toggleLayer = "defaultmap";
      }
      if (satelliteAvailable) {
        disableSatellite = false;
        if (satelliteSelected && satelliteTileUrl) {
          effectiveLayer = "satellite";
          toggleLayer = "satellite";
        }
      }
    } else if (!!drawingMode && isDrawing && planetAvailable && !planetFailed) {
      disableSatellite = false;
      if (planetTileUrl) {
        effectiveLayer = "planet";
        toggleLayer = "satellite";
      }
    }
  }

  return {
    disableSatellite,
    effectiveLayer,
    toggleLayer,
  };
};

export const {
  setOverlayMode,
  reverseFillVectors,
  setTileLayer,
  setTileBan,
  clearTileBan,
  hideTileBan,
  setSatelliteTilesAvailable,
  setImageDate,
  setFieldsLayerKey,
  setOwnersLayerKey,
  setAOILayer,
  setCropTypes,
  setFieldsLegends,
  setIrrigationMethods,
  setMapBounds,

  // map
  setRestoreView,
  setMapCenter,
  setMapZoom,
  clearAllMapState,
  setTargetBounds,
  clearTargetBounds,

  // tiles
  setTilesLimits,
  addTile,
  setTiles,
  updateTileUrl,
  setSatelliteTileTemplate,
  setPlanetTileTemplate,
  setPlanetTileFailed,
  setDefaultMapTileTemplate,
  setAddProjectSatelliteTileTemplate,
  setAddProjectPlanetTileTemplate,
  setAddProjectPlanetTileFailed,
  deleteTile,
  reorderTiles,
  toggleTileVisibility,
  setAllTilesVisibility,
  setCurrentField,
  setCurrentWorkspace,
  setBoundaryMethod,
  setDrawingMode,
  setDrawnShape,
  clearDrawnShape,
  clearAreaDrawing,
  setEditingEntity,
  clearEditingEntity,
} = mapSlice.actions;

export default mapSlice.reducer;
